import React, { useMemo, useContext } from "react";
import { intl, LanguageBaseContext } from "dmsbasereact";
import { ReportLayout } from "./components/ReportLayout";
import { getInstitutionCategoryLabel } from "../../constants/InstitutionType";
import { useFormatters } from "../../hooks/useFormater";


export const InstitutionsReportPage = () => {
    // Get language context to re-render when language changes
    const { language } = useContext(LanguageBaseContext);
    const formatters = useFormatters();


    // Column order matching .ai_docs/reports.md
    const header = useMemo(() => [
        {
            label: intl.get("Reports.Institutions.Name").d("Institution Name"),
            column_name: "name",
            width: "200px",
        },
        {
            label: intl.get("Reports.Institutions.CreatedAt").d("Created At"),
            column_name: "created_at",
            width: "110px",
            function: (value) => formatters.formatDate(value),
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "cnpj",
            width: "160px",
            function: (value) => formatters.formatCNPJ(value),
        },
        {
            label: intl.get("Reports.Institutions.Street").d("Street"),
            column_name: "street",
            width: "180px",
        },
        {
            label: intl.get("Reports.Institutions.City").d("City"),
            column_name: "city",
            width: "150px",
        },
        {
            label: intl.get("Reports.Institutions.State").d("State"),
            column_name: "state",
            width: "60px",
        },
        {
            label: intl.get("Reports.Institutions.PostalCode").d("Zip Code"),
            column_name: "postal_code",
            width: "90px",
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status",
            width: "100px",
        },
        {
            label: intl.get("Reports.Institutions.InactivationDate").d("Inactivation Date"),
            column_name: "inactivation_date",
            width: "120px",
        },
        {
            label: intl.get("Reports.Institutions.InactivationReason").d("Inactivation Reason"),
            column_name: "inactivation_reason",
            width: "200px",
        },
        {
            label: intl.get("Reports.Institutions.Category").d("Category"),
            column_name: "categories",
            width: "140px",
            function: (value) => {
                if (Array.isArray(value) && value.length > 0) {
                    return value.map(getInstitutionCategoryLabel).join(", ");
                }
                return getInstitutionCategoryLabel(value);
            },
        },
        {
            label: intl.get("Reports.Institutions.CRTValidity").d("CRT Validity"),
            column_name: "crt_validity",
            width: "110px",
        },
        {
            label: intl.get("Reports.Institutions.SanitaryLicenseValidity").d("Sanitary License Validity"),
            column_name: "sanitary_license_validity",
            width: "110px",
        },
        {
            label: intl.get("Reports.Institutions.DoctorsList").d("Associated Doctors"),
            column_name: "doctors_list",
            width: "250px",
        },
        {
            label: intl.get("Reports.Institutions.PharmacistsList").d("Associated Pharmacists"),
            column_name: "pharmacists_list",
            width: "250px",
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [formatters, language]);

    const filterFields = useMemo(() => [
        { name: "name", label: intl.get("Name").d("Name"), type: "text" },
        { name: "cnpj", label: intl.get("CNPJ").d("CNPJ"), type: "text" },
        {
            name: "category",
            label: intl.get("Category").d("Category"),
            type: "multiselect",
            optionsKey: "category",
        },
        {
            name: "city",
            label: intl.get("City").d("City"),
            type: "multiselect",
            optionsKey: "city",
        },
        {
            name: "state",
            label: intl.get("State").d("State"),
            type: "multiselect",
            optionsKey: "state",
        },
        { name: "status", label: intl.get("Status").d("Status"), type: "multiselect", optionsKey: "status" },
    ], []);

    return (
        <ReportLayout
            key={`institutions-report-${language}`}
            title="Reports.Institutions.PageTitle"
            titleDefault="Institutions Report"
            manualSlug="reports-institutions"
            tableUrl="/reports/institutions/table"
            filterOptionsUrl="/reports/institutions/filter-options"
            exportUrl="/reports/institutions/export"
            header={header}
            filterFields={filterFields}
            dateFilterLabel={intl.get("Reports.DateFilter.RegistrationDate").d("Registration Date")}
        />
    );
};

export default InstitutionsReportPage;
